import { Alert, AlertIcon, Box, Button, HStack, Icon, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiCheckCircle, FiFileText } from "react-icons/fi";
import {
  bffApi,
  type SourcingEventDto,
  SourcingEventStatus,
  useRecordWinnerDocumentationCheckedMutation,
  type WinnerDocumentationDto,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { DocumentationNoteField, documentationNoteTooLong } from "./DocumentationNoteField";
import { useAppDispatch } from "../../../common/redux/hooks";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";
import { useConfirmationModal } from "../../../common/useConfirmationModal";
import { REVEAL_ANCHORS } from "../revealAnchor";
import { isCheckAlreadyRecorded, isCheckForAnotherAward } from "./winnerDocumentationRefusal";
import { useWinnerDocumentationTexts } from "./useWinnerDocumentationTexts";

export const WinnerDocumentationCheck = ({
  event,
  check,
  chosenSuppliers,
  readOnly,
}: {
  event: SourcingEventDto;
  check: WinnerDocumentationDto;
  chosenSuppliers: string[];
  readOnly: boolean;
}) => {
  const { t } = useTranslation();
  const displayDateTime = useDisplayDateWithTime();
  const { trigger } = useApiError();
  const confirmationModal = useConfirmationModal();
  const dispatch = useAppDispatch();
  const [note, setNote] = useState("");
  const [discardedNote, setDiscardedNote] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<"AWARD_CHANGED" | "ALREADY_RECORDED" | null>(null);
  const suppliers = chosenSuppliers.join(", ");
  const texts = useWinnerDocumentationTexts(check.rule);
  const [record, { isLoading }] = useRecordWinnerDocumentationCheckedMutation();
  const done = check.checkedAt != null;
  const stillAhead = event.status === SourcingEventStatus.AwardDecision;

  const submit = async () => {
    setRefusal(null);
    setDiscardedNote(null);
    try {
      await record({
        eventId: event.id,
        recordWinnerDocumentationCheckRequest: {
          awardedBidIds: event.award?.winningBidIds ?? [],
          note: note.trim() || null,
        },
      }).unwrap();
    } catch (error) {
      const refused = isCheckForAnotherAward(error)
        ? "AWARD_CHANGED"
        : isCheckAlreadyRecorded(error)
          ? "ALREADY_RECORDED"
          : null;
      if (refused) {
        setRefusal(refused);
        setDiscardedNote(note.trim() || null);
        setNote("");
        dispatch(bffApi.util.invalidateTags(["SourcingEvent"]));
        return;
      }
      trigger(error);
    }
  };

  const requestConfirmation = () =>
    confirmationModal.show({
      title: suppliers
        ? t("Record that you have obtained and checked the documentation from {{suppliers}}?", { suppliers })
        : t("Record that you have obtained and checked the chosen supplier's documentation?"),
      description: t(
        "The check goes into the procurement protocol and can't be withdrawn. Only changing the award decision clears it.",
      ),
      onConfirmed: () => void submit(),
    });

  return (
    <Box
      id={REVEAL_ANCHORS.winnerDocumentation}
      sx={{ scrollMarginTop: "1rem" }}
      bg="smBackground"
      rounded="xl"
      border="1px solid"
      borderColor="smBorder"
      p="5"
    >
      <HStack spacing="3" align="flex-start">
        <Icon as={done ? FiCheckCircle : FiFileText} color={done ? "smSuccess" : "smMuted"} mt="1" />
        <Box flex="1">
          <Text fontWeight="semibold" color="smText">
            {texts.heading(suppliers)}
          </Text>
          {refusal && (
            <Alert status="warning" rounded="md" mt="3" fontSize="sm" alignItems="flex-start">
              <AlertIcon />
              <Box>
                <Text>
                  {refusal === "AWARD_CHANGED"
                    ? t(
                        "The award has changed since this page was loaded, so the check was not recorded. Check the documentation from the supplier now chosen.",
                      )
                    : t(
                        "Someone recorded the check after this page was loaded, so yours was not recorded. Their check is shown below.",
                      )}
                </Text>
                {discardedNote && (
                  <Text mt="1" color="smMuted" whiteSpace="pre-wrap" overflowWrap="anywhere">
                    {t("Your note was not saved: {{note}}", { note: discardedNote })}
                  </Text>
                )}
              </Box>
            </Alert>
          )}
          {done ? (
            <>
              <Text fontSize="sm" color="smText" mt="2">
                {check.checkedByName
                  ? t("Checked {{date}} by {{name}}.", {
                      date: displayDateTime(check.checkedAt ?? ""),
                      name: check.checkedByName,
                    })
                  : t("Checked {{date}}.", { date: displayDateTime(check.checkedAt ?? "") })}
              </Text>
              {check.note && (
                <Text fontSize="sm" color="smMuted" mt="1" whiteSpace="pre-wrap" overflowWrap="anywhere">
                  {check.note}
                </Text>
              )}
            </>
          ) : stillAhead ? (
            <Text fontSize="sm" color="smMuted" mt="2">
              {texts.beforeAward}
            </Text>
          ) : (
            <Text fontSize="sm" color="smMuted" mt="2">
              {t("Not recorded. The award was made without a recorded check of this documentation.")}
            </Text>
          )}
          {check.canRecord && !readOnly && (
            <>
              <DocumentationNoteField note={note} onChange={setNote} mt="4" />
              <Button
                mt="3"
                colorScheme="teal"
                isLoading={isLoading}
                isDisabled={documentationNoteTooLong(note)}
                onClick={requestConfirmation}
              >
                {t("Record the documentation as checked")}
              </Button>
            </>
          )}
        </Box>
      </HStack>
      {confirmationModal.modal}
    </Box>
  );
};
